/**
 * A branch guest session: branch staff opening a customer's account to manage their favourites,
 * without needing the customer's password.
 *
 * The session itself is an ordinary customer session (see /skeem/generateLoginToken), so these
 * limits are presentational: the customer area hides everything that writes apart from
 * favourites, and shows a banner saying whose account is open. Anyone who can start a guest
 * session could still act as the customer through the API.
 */

const BRANCH_GUEST_SESSION_KEY = "FOS-BRANCH-GUEST-SESSION"
const EOS_SKEEM_TOKEN_KEY = "EOS-SKEEM-TOKEN"

/**
 * How long a guest session lasts. Signing in rewrites the token's own expiry, so this is what
 * actually ends the session.
 */
export const BRANCH_GUEST_SESSION_MAX_AGE = 1000 * 60 * 60 * 2

/**
 * Customer-area routes a guest can't open, because they create or change something other than
 * favourites. Matched on whole path segments.
 */
const BLOCKED_ROUTE_PREFIXES = [
  "/checkout",
  "/cart",
  "/my-account",
  "/clients",
  "/users",
  "/rma",
  "/application",
  "/support-and-returns/customer-support/rma",
  "/jobs/new",
  "/orders/new",
  "/quotes/new",
]

export function isBranchGuestBlockedRoute(pathname: string) {
  const path = (pathname || "/").replace(/\/+$/, "") || "/"
  // Any create or edit page, e.g. /jobs/123/edit or /invoices/123/new
  if (path.split("/").some(segment => segment === "new" || segment === "edit")) return true
  return BLOCKED_ROUTE_PREFIXES.some(prefix => path === prefix || path.startsWith(`${prefix}/`))
}

export interface IBranchGuestSession {
  /** The customer whose account is open, for the banner */
  customerName: string
  /** Where to send staff back to when they leave */
  returnTo: string
  startedAt: number
  /**
   * The staff member's own session token, put back when they leave so they return to EOS still
   * signed in rather than having to log in again.
   */
  staffToken?: string | null
}

export function startBranchGuestSession(session: IBranchGuestSession) {
  try {
    window.localStorage.setItem(BRANCH_GUEST_SESSION_KEY, JSON.stringify(session))
  } catch (e) {
    console.error(e)
  }
}

export function getBranchGuestSession(): IBranchGuestSession | null {
  try {
    const raw = window.localStorage.getItem(BRANCH_GUEST_SESSION_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed.customerName !== "string") return null

    // Signing in rewrites the token's expiry, so an old guest record would otherwise keep the
    // customer's session alive with no banner and no limits. Sign out instead.
    if (isBranchGuestSessionExpired(parsed.startedAt)) {
      clearBranchGuestSession()
      window.localStorage.removeItem(EOS_SKEEM_TOKEN_KEY)
      return null
    }
    return parsed as IBranchGuestSession
  } catch (e) {
    console.error(e)
    return null
  }
}

export function isBranchGuestSessionExpired(startedAt: unknown, now = Date.now()) {
  if (typeof startedAt !== "number" || !Number.isFinite(startedAt)) return true
  return now - startedAt > BRANCH_GUEST_SESSION_MAX_AGE
}

export function clearBranchGuestSession() {
  try {
    window.localStorage.removeItem(BRANCH_GUEST_SESSION_KEY)
  } catch (e) {
    console.error(e)
  }
}
